var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/extensions/AccordeonExtension.ts
import { logger } from "@adornis/base/logging.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-accordeon.js";
import "@adornis/chemistry/elements/components/x-button.js";
import "@adornis/chemistry/elements/components/x-hr.js";
import { DesignSystem } from "@adornis/chemistry/elements/theming/design.js";
import "@adornis/forms/x-input.js";
import "@adornis/popover/x-dropdown-selection.js";
import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { filter, firstValueFrom } from "rxjs";
import { BaseAccordeon, BaseAccordeonItem } from "../../db/extensions/BaseAccordeon.js";
import "../components/x-buildify-permission-selection.js";
import "../components/x-icon-button.js";
import "../components/x-infobox.js";
import { __renderBase } from "../globals/methods.js";
import "./../x-buildify-dropzone.js";
import { XContainerExtension } from "./ContainerExtension.js";
import { EditExtensionData } from "./EditExtensionData.js";
import "./TextExtension.js";
var AccordeonExtension = (textExtension) => ({
  class: BaseAccordeon,
  toolbar: { icon: "keyboard_arrow_down", text: { default: "Accordeon" } },
  render(instance, { isSubelement, dropzoneBefore, dropzoneAfter }) {
    return html`
      <x-accordeon-extension
        .textExtension=${textExtension}
        .content=${instance}
        .defaultValues=${this.defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></x-accordeon-extension>
    `;
  },
  editor({ content, contentController, controllerBaseKeyPath, host, globalSettings, extensions }) {
    return html`
      <x-flex space="md">
        <x-infobox>
          Wenn ein bestimmtes Accordeon bereits beim Öffnen der Seite offen visualisiert werden soll, kannst du hier die
          Nummer des betroffenen Accordeons angeben.
        </x-infobox>
        <x-input
          placeholder="Geöffnet"
          type="number"
          ${contentController.field(...controllerBaseKeyPath, "openIndex")}
        ></x-input>
        ${repeat(
      content.accordeonItems ?? [],
      (item) => item._id,
      (item, index) => html`
            <x-flex space="sm">
              <x-flex horizontal crossaxis-center space-between>
                <x-text> <b>${index + 1}</b>. Accordeon </x-text>
                <x-icon-button
                  icon="delete"
                  background=${host.colors.tone.error}
                  color="#fff"
                  @click=${() => {
        content.accordeonItems.splice(index, 1);
        host.requestUpdate();
      }}
                ></x-icon-button>
              </x-flex>
              ${EditExtensionData({
        host,
        content: {
          value: item.title,
          path: [...controllerBaseKeyPath, "accordeonItems", index, "title"],
          controller: contentController,
          extension: textExtension
        },
        globalSettings,
        extensions
      })}
              <x-buildify-permission-selection
                ${contentController.field(...controllerBaseKeyPath, "accordeonItems", index, "permission")}
              ></x-buildify-permission-selection>
              <x-hr></x-hr>
            </x-flex>
          `
    )}
        <x-icon-button
          ${css({ alignSelf: "end" })}
          icon="add"
          background="#bbbbb4"
          color="#333"
          tooltip="weiteres Accordeon hinzufügen"
          @click=${() => {
      content.accordeonItems.push(new BaseAccordeonItem({}));
      host.requestUpdate();
    }}
        >
        </x-icon-button>
      </x-flex>
    `;
  },
  group: "Layout"
});
var XAccordeonExtension = class extends XContainerExtension {
  constructor() {
    super(...arguments);
    this.ref = createRef();
  }
  _content(content) {
    const items = content.accordeonItems ?? [];
    const accordeonItems = items.map((item) => {
      const titleRender = html`
        ${this.textExtension?.render(item.title, { defaultValues: this.defaultValues.value ?? {}, isSubelement: true })}
      `;
      const consumedContents = this._consumedContent.value ?? [];
      const childElement = consumedContents.find((base) => base.parentID === content._id && base.subItemID === item._id);
      return {
        title: titleRender,
        text: () => html`
            <div ${css({ zIndex: "100" })}>
              ${childElement ? __renderBase(this._consumedExtensions.value, childElement, { isSubelement: false }) : html`
                    <x-buildify-dropzone
                      .index=${0}
                      .parentID=${content._id}
                      .subItemID=${item._id}
                    ></x-buildify-dropzone>
                  `}
            </div>
          `
      };
    });
    return html`
      <x-accordeon
        ${ref(this.ref)}
        .selected=${content.openIndex ? content.openIndex - 1 : -1}
        .items=${accordeonItems}
      >
      </x-accordeon>
    `;
  }
  async activate(desiredChildID) {
    if (!desiredChildID)
      return logger.warn("no desired child id given, cant activate sub element");
    await firstValueFrom(this._connected.pipe(filter(Boolean)));
    await this.updateComplete;
    await new Promise((r) => requestAnimationFrame(() => r()));
    const thisContent = this._convertedContent;
    const subItems = this._convertedContent?.accordeonItems ?? [];
    const allContents = this._consumedContent.value ?? [];
    const nth = subItems.findIndex((subItem) => {
      const fittingContent = allContents.find(
        (content) => content.parentID === thisContent?._id && content.subItemID === subItem._id && content._id === desiredChildID
      );
      return !!fittingContent;
    });
    this.ref.value.selected = nth;
    const toggleContent = this.ref.value?.shadowRoot?.querySelector(
      `${DesignSystem.prefix}-toggle-content:nth-of-type(${nth + 1})`
    );
    if (!toggleContent)
      return;
    await firstValueFrom(toggleContent._connected.pipe(filter(Boolean)));
    await toggleContent.updateComplete;
    await new Promise((r) => requestAnimationFrame(() => r()));
    return toggleContent?.shadowRoot;
  }
  styles() {
    return [
      ...super.styles(),
      {
        ":host": {
          width: "100%",
          boxSizing: "border-box"
        }
      }
    ];
  }
};
__decorateClass([
  property({ attribute: false })
], XAccordeonExtension.prototype, "textExtension", 2);
XAccordeonExtension = __decorateClass([
  customElement("x-accordeon-extension")
], XAccordeonExtension);
export {
  AccordeonExtension,
  XAccordeonExtension
};
